/**
 * Crew Inbox Page
 *
 * Mobile-first inbox for crew members to view messages from admins.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMyMessages,
  type InboxMessage,
  type MessageType,
  type GetMyMessagesParams,
} from '../../../api/messages';
import { getErrorMessage } from '../../../api/client';
import { useOrganization } from '../../../context/OrganizationContext';

const MESSAGE_TYPE_CONFIG: Record<MessageType, { label: string; className: string }> = {
  BROADCAST: { label: 'Direct Message', className: 'bg-blue-100 text-blue-800' },
  ASSIGNMENT: { label: 'Assignment', className: 'bg-purple-100 text-purple-800' },
  CREW_REPLY: { label: 'Crew Reply', className: 'bg-green-100 text-green-800' },
  EVENT_PUBLISHED: { label: 'New Event', className: 'bg-teal-100 text-teal-800' },
  EVENT_UPDATED: { label: 'Event Update', className: 'bg-amber-100 text-amber-800' },
};

export default function InboxPage() {
  const navigate = useNavigate();
  const { organizations, hasMultipleOrgs } = useOrganization();

  // State
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [orgFilter, setOrgFilter] = useState<string>('all');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: GetMyMessagesParams = {
        page,
        limit,
        unreadOnly: unreadOnly || undefined,
      };

      if (orgFilter !== 'all') {
        params.orgSlug = orgFilter;
      }

      const response = await getMyMessages(params);
      setMessages(response.messages);
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page, unreadOnly, orgFilter]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isThisYear = date.getFullYear() === now.getFullYear();

    if (isToday) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      });
    }

    if (isThisYear) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Reset page when filters change
  const handleUnreadChange = (value: boolean) => {
    setUnreadOnly(value);
    setPage(1);
  };

  const handleOrgChange = (value: string) => {
    setOrgFilter(value);
    setPage(1);
  };

  if (loading && messages.length === 0) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="h-12 bg-gray-200 rounded"></div>
        <div className="h-24 bg-gray-200 rounded"></div>
        <div className="h-24 bg-gray-200 rounded"></div>
        <div className="h-24 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">Messages</h1>
        <p className="text-gray-600 text-sm mt-1">
          Stay updated with announcements and notifications
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* Unread toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => handleUnreadChange(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-rack"
            />
            <span className="text-sm text-gray-700">Unread only</span>
          </label>

          {/* Org filter (only show if user has multiple orgs) */}
          {hasMultipleOrgs && (
            <select
              value={orgFilter}
              onChange={(e) => handleOrgChange(e.target.value)}
              className="text-sm border border-gray-300 rounded-md pl-3 pr-10 py-1.5 focus:ring-rack focus:border-rack"
            >
              <option value="all">All Organizations</option>
              {organizations.map((org) => (
                <option key={org.orgSlug} value={org.orgSlug}>
                  {org.orgName}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Messages List */}
      {messages.length > 0 ? (
        <div className="space-y-3">
          {messages.map((message) => {
            const typeConfig = MESSAGE_TYPE_CONFIG[message.messageType];
            const isUnread = !message.readAt;

            return (
              <button
                key={message.id}
                onClick={() => navigate(`/crew/messages/${message.id}`)}
                className={`w-full text-left bg-white rounded-lg shadow-sm border p-4 transition-colors hover:bg-gray-50 ${
                  isUnread ? 'border-primary-300 border-2' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Unread indicator */}
                  {isUnread && (
                    <div className="flex-shrink-0 mt-2">
                      <div className="w-2 h-2 bg-primary-600"></div>
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    {/* Subject and type */}
                    <div className="flex items-start justify-between gap-2">
                      <h3 className={`text-sm ${isUnread ? 'font-semibold' : 'font-medium'} text-gray-900 line-clamp-1`}>
                        {message.subject}
                      </h3>
                      <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium ${typeConfig.className}`}>
                        {typeConfig.label}
                      </span>
                    </div>

                    {/* Body preview */}
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {message.body}
                    </p>

                    {/* Meta info */}
                    <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                      <span>
                        {message.sentBy.firstName} {message.sentBy.lastName}
                        {hasMultipleOrgs && ` • ${message.organization.name}`}
                      </span>
                      <span>{formatDate(message.sentAt)}</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No messages</h3>
          <p className="mt-1 text-sm text-gray-500">
            {unreadOnly
              ? "You're all caught up!"
              : "You haven't received any messages yet"}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-gray-500">
            {(page - 1) * limit + 1}-{Math.min(page * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
